import PostHog from './posthog';

const METHODS = {
  '---': 'no_payment',
  'Credit Card': 'card',
  'Saved Credit Card': 'saved_card',
  'Amazon v2': 'amazon_pay',
  Affirm: 'affirm',
  'Bank Account': 'bank_account',
  'Payment Request Button': 'payment_request',
};

const METHOD_IDS = new Set([...Object.values(METHODS), 'apple_pay', 'google_pay']);
const normalizeMethod = type => METHOD_IDS.has(type) ? type : METHODS[type] ?? 'unknown';

// Match parabo-swift's capture contract, including string amounts and IDs.
const priced = (value, currency) => ({
  ...(value != null ? { value: `${value}` } : {}),
  ...(currency ? { currency } : {}),
});

// Set only while a checkout page with a card form is mounted, so outcomes from
// the shared card component carry the experiment arm without a new prop.
// Attempts snapshot it when they start, so leaving the page mid-request keeps it.
let paymentForm = null;
// An app forwards failures to its own providers (parabo-web GA4) without the shared
// components knowing about them.
let failureListener = null;
// Stripe.js errors carry decline_code (card declines) or code. API wrappers pass their own
// errorCode next to a display string (see prepare), a bare string is a backend message.
const stripeErrorCode = error => {
  if (typeof error === 'string') return 'server_error';
  return error?.decline_code ?? error?.code ?? 'unknown';
};

const withPaymentForm = (properties, form) => (form ? { ...properties, payment_form: form } : properties);

const CheckoutAnalytics = {
  setPaymentForm(form) {
    paymentForm = form ?? null;
  },

  currentPaymentForm() {
    return paymentForm;
  },

  paymentFormExposed(form) {
    PostHog.customEvent('checkout_payment_form_exposed', { payment_form: form });
  },

  // Same method IDs as the PostHog events, so GA4 and PostHog break down alike.
  paymentMethod(type) {
    return normalizeMethod(type);
  },

  walletMethod(response) {
    const wallet = response.token?.card?.tokenization_method ?? response.walletName;
    if (wallet === 'apple_pay' || wallet === 'applePay') return 'apple_pay';
    if (wallet === 'android_pay' || wallet === 'google_pay' || wallet === 'googlePay') return 'google_pay';
    return 'payment_request';
  },

  // canMakePayment() resolves to the wallets the button can open, e.g. { applePay: true, googlePay: false, link: false }.
  buttonWallet(result) {
    if (result?.applePay) return 'apple_pay';
    if (result?.googlePay) return 'google_pay';
    if (result?.link) return 'link';
    return 'payment_request';
  },

  started(value, currency) {
    PostHog.customEvent('checkout_init', priced(value, currency));
  },

  succeeded(type, value, currency, orderId, form) {
    PostHog.customEvent('checkout_payment', withPaymentForm({
      method: normalizeMethod(type),
      ...priced(value, currency),
      ...(orderId != null ? { order_id: `${orderId}` } : {}),
    }, form));
  },

  setFailureListener(listener) {
    failureListener = listener ?? null;
  },

  // `errorCode`: backend reason, Stripe decline_code / code, or the failure class.
  failed(type, form, errorCode) {
    const method = normalizeMethod(type);
    PostHog.customEvent('checkout_payment_failure', withPaymentForm({
      method,
      ...(errorCode ? { error_code: errorCode } : {}),
    }, form));
    failureListener?.(method, errorCode);
  },

  async prepare(type, operation) {
    const form = paymentForm;
    let result;
    try {
      result = await operation();
    } catch (error) {
      if (error?.type !== 'validation_error') CheckoutAnalytics.failed(type, form, stripeErrorCode(error));
      throw error;
    }
    if (result?.error && result.error.type !== 'validation_error') {
      CheckoutAnalytics.failed(type, form, result.errorCode ?? stripeErrorCode(result.error));
    }
    return result;
  },
};

export default CheckoutAnalytics;
